import { writable, derived, type Readable, type Writable } from 'svelte/store';

/**
 * ダークモード基盤（docs/pm/CONTRACTS.md §6）
 *
 * - `themeMode` : ユーザーが選択したモード。'system' | 'light' | 'dark'。localStorageに永続化する。
 * - `resolvedTheme` : 実際にDOMへ適用される 'light' | 'dark'。'system'選択時はOSのprefers-color-schemeに追従する。
 *
 * 利用側（各feature）はCSS変数（theme.css）を参照するだけでよく、
 * このモジュールを直接使うのはテーマ切替UI（SETTINGS）程度を想定している。
 */

export type ThemeMode = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'expense-tracker:theme-mode';

function isThemeMode(value: string | null): value is ThemeMode {
  return value === 'system' || value === 'light' || value === 'dark';
}

function readInitialMode(): ThemeMode {
  if (typeof localStorage === 'undefined') return 'system';
  const saved = localStorage.getItem(STORAGE_KEY);
  return isThemeMode(saved) ? saved : 'system';
}

export const themeMode: Writable<ThemeMode> = writable(readInitialMode());

themeMode.subscribe((mode) => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, mode);
});

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** 実際にDOMへ適用される 'light' | 'dark'（'system'選択時はOS設定を反映） */
export const resolvedTheme: Readable<'light' | 'dark'> = derived(themeMode, ($mode, set) => {
  if ($mode !== 'system') {
    set($mode);
    return;
  }

  set(systemPrefersDark() ? 'dark' : 'light');

  if (typeof window === 'undefined' || !window.matchMedia) return;
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const handleChange = (event: MediaQueryListEvent) => set(event.matches ? 'dark' : 'light');
  mql.addEventListener('change', handleChange);
  return () => mql.removeEventListener('change', handleChange);
});

/** `document.documentElement` に `data-theme` 属性としてテーマを適用する */
export function applyThemeToDocument(theme: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

/**
 * アプリ起動時に一度呼ぶ。`resolvedTheme` の変化をDOMへ反映し続ける購読を開始する。
 * 戻り値の関数を呼ぶと購読を解除できる。
 */
export function initTheme(): () => void {
  return resolvedTheme.subscribe(applyThemeToDocument);
}
